import { TIcon } from '@acceso/design-system';

interface TMenuItem {
  name: string;
  icon: TIcon;
  permissions?: string[];
  routeName?: string;
}

type TMenu = {
  submenu: TMenuItem[];
} & TMenuItem;

type TCountry = {
  code: string;
  name: string;
  idd: { root: string; suffixes: string[] };
  flag: string;
  currencies: string[];
};

type TCurrency = {
  code: string;
  name: string;
  symbol: string;
};

type TTimezone = {
  offset: string;
  name: string;
};

type THeadquarters = {
  code: string;
  code_api: {
    country: string;
  };
  name: string;
  parent_code: string | null;
};

type THoliday = {
  date: string;
  start: string;
  end: string;
  name: string;
  type: string;
  substitute?: boolean;
  rule: string;
};

enum SegmentationTypeEnum {
  VIP = 'vip',
  A = 'a',
  B = 'b',
  C = 'c',
}

type TClientState = 'active' | 'inactive';

type TClientParameters = {
  logo_positive?: string | null;
};

enum ClientTypeEnum {
  AGENCY = 'agency',
  END_CLIENT = 'end_client',
  INTERNAL = 'internal',
}

type TClientSimpleEntity = {
  id: string;
  name: string;
  parameters?: TClientParameters;
};

type TClient = {
  id: string;
  created_by: string;
  created_at: string;
  updated_by?: string;
  updated_at?: string;
  name: string;
  description?: string | null;
  code: string;
  sector: string[] | string | null;
  state: TClientState;
  state_changed_at: string | null;
  parameters?: TClientParameters;
  segmentation: SegmentationTypeEnum;
  client_type: ClientTypeEnum;
};

type TSearchSimpleEntity = {
  id: string;
  name: string;
};

type WorkspaceState = 'active' | 'inactive';

type WorkspaceParameters = {
  logo_positive?: File | string | null;
  dossier_public_link: boolean;
  contact_limit: number | null;
  user_limit: number | null;
};

enum WorkspaceTypeEnum {
  STANDARD = 'standard',
  THEMATIC = 'thematic',
}

type TWorkspaceSimpleEntity = {
  id: string;
  name: string;
};

type TWorkspaceEntity = {
  id: string;
  created_by: string;
  created_at: string;
  updated_by: string;
  updated_at: string;
  client_id: string;
  name: string;
  description: string | null;
  code: string;
  state: WorkspaceState;
  state_changed_at: string;
  parameters: WorkspaceParameters;
  country_code: string;
  currency_code: string;
  timezone_code: string;
  language_code: string;
  headquarters: string;
  workspace_type: WorkspaceTypeEnum;
};

type TProject = {
  id: string;
  created_at: string;
  created_by: string;
  updated_at: string;
  updated_by: string;
  deleted_at: string;
  deleted_by: string;
  workspace_id: string;
  name: string;
  code: string | null;
  state: 'active' | 'inactive';
  start_date: string | null;
  end_date: string | null;
  state_changed_at: string;
  description: string | null;
  parameters: Partial<TProjectParameters> | null;
  client_id: string;
};

type TProjectParameters = {
  categories: TReboldCategories;
  sponsorship: boolean;
};

type TReboldCategories = {
  reptrack: boolean;
  sasb: boolean;
  activity_axes: boolean;
};

type TInformationUnitTag = {
  id: string;
  created_by: string;
  created_at: string;
  updated_by: string;
  updated_at: string;
  workspace_id: string;
  name: string;
  state: 'active' | 'inactive';
  state_changed_at: string | null;
  visibility: 'public' | 'private';
  pinned: boolean;
  groupable: boolean;
  information_unit_tag_groups: string[];
  automatic: boolean;
};

export type {
  TSearchSimpleEntity,
  TInformationUnitTag,
  TClient,
  TClientSimpleEntity,
  TCountry,
  TCurrency,
  THeadquarters,
  THoliday,
  TMenu,
  TMenuItem,
  TTimezone,
  TWorkspaceEntity,
  TWorkspaceSimpleEntity,
  TProject,
};

export { WorkspaceTypeEnum };
